import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  Notification,
  NotificationCounts,
} from '@dataTypes/notification.types'
import {
  dismissNotificationThunk,
  fetchNotificationCounts,
  fetchNotifications,
  markAllNotificationsReadThunk,
  markNotificationReadThunk,
  markNotificationUnreadThunk,
} from '../actions/notification.action'

export interface NotificationState {
  items: Notification[]
  counts: NotificationCounts
  loading: boolean
  error: string | null
  lastFetchedAt: string | null
}

const emptyCounts: NotificationCounts = {
  critical: 0,
  urgent: 0,
  low: 0,
  total: 0,
  unread: 0,
}

const initialState: NotificationState = {
  items: [],
  counts: { ...emptyCounts },
  loading: false,
  error: null,
  lastFetchedAt: null,
}

const upsert = (items: Notification[], next: Notification): Notification[] => {
  const idx = items.findIndex(
    (n) =>
      (n.notification_id && n.notification_id === next.notification_id) ||
      n.id === next.id,
  )
  if (idx === -1) return [next, ...items]
  const copy = items.slice()
  copy[idx] = { ...copy[idx], ...next }
  return copy
}

const recomputeCountsFromItems = (
  items: Notification[],
): NotificationCounts => {
  const counts = { ...emptyCounts }
  for (const n of items) {
    if (n.dismissed) continue
    counts.total += 1
    if (!n.is_read) counts.unread += 1
    switch (n.priority) {
      case 'critical':
        counts.critical += 1
        break
      case 'urgent':
        counts.urgent += 1
        break
      case 'low':
        counts.low += 1
        break
    }
  }
  return counts
}

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    addRealtimeNotification(state, action: PayloadAction<Notification>): void {
      const next = action.payload
      state.items = upsert(state.items, next)
      state.counts = recomputeCountsFromItems(state.items)
    },
    applyRealtimeRead(state, action: PayloadAction<{ id: string }>): void {
      state.items = state.items.map((n) =>
        n.notification_id === action.payload.id || n.id === action.payload.id
          ? { ...n, is_read: true }
          : n,
      )
      state.counts = recomputeCountsFromItems(state.items)
    },
    applyRealtimeUnread(state, action: PayloadAction<{ id: string }>): void {
      state.items = state.items.map((n) =>
        n.notification_id === action.payload.id || n.id === action.payload.id
          ? { ...n, is_read: false }
          : n,
      )
      state.counts = recomputeCountsFromItems(state.items)
    },
    applyRealtimeDismissed(state, action: PayloadAction<{ id: string }>): void {
      state.items = state.items.map((n) =>
        n.notification_id === action.payload.id || n.id === action.payload.id
          ? { ...n, dismissed: true }
          : n,
      )
      state.counts = recomputeCountsFromItems(state.items)
    },
    applyRealtimeReadAll(state): void {
      state.items = state.items.map((n) => ({ ...n, is_read: true }))
      state.counts = recomputeCountsFromItems(state.items)
    },
    clearNotifications(state): void {
      state.items = []
      state.counts = { ...emptyCounts }
      state.error = null
      state.loading = false
      state.lastFetchedAt = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<Notification[]>) => {
          state.items = action.payload
          state.loading = false
          state.lastFetchedAt = new Date().toISOString()
          state.counts = recomputeCountsFromItems(action.payload)
        },
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to load notifications'
      })

    builder.addCase(
      fetchNotificationCounts.fulfilled,
      (state, action: PayloadAction<NotificationCounts>) => {
        state.counts = action.payload
      },
    )

    builder.addCase(markNotificationReadThunk.fulfilled, (state, action) => {
      const id = action.payload
      state.items = state.items.map((n) =>
        n.notification_id === id || n.id === id ? { ...n, is_read: true } : n,
      )
      state.counts = recomputeCountsFromItems(state.items)
    })

    builder.addCase(markNotificationUnreadThunk.fulfilled, (state, action) => {
      const id = action.payload
      state.items = state.items.map((n) =>
        n.notification_id === id || n.id === id ? { ...n, is_read: false } : n,
      )
      state.counts = recomputeCountsFromItems(state.items)
    })

    builder.addCase(dismissNotificationThunk.fulfilled, (state, action) => {
      const id = action.payload
      state.items = state.items.map((n) =>
        n.notification_id === id || n.id === id ? { ...n, dismissed: true } : n,
      )
      state.counts = recomputeCountsFromItems(state.items)
    })

    builder.addCase(markAllNotificationsReadThunk.fulfilled, (state) => {
      state.items = state.items.map((n) => ({ ...n, is_read: true }))
      state.counts = recomputeCountsFromItems(state.items)
    })
  },
})

export const {
  addRealtimeNotification,
  applyRealtimeRead,
  applyRealtimeUnread,
  applyRealtimeDismissed,
  applyRealtimeReadAll,
  clearNotifications,
} = notificationSlice.actions

export default notificationSlice.reducer
